import {
  Player,
  PlayerFiltersPosition,
  PlayerFiltersViewDefaultAllOption,
} from '@dreamteam-monorepo/ui';
import { create } from 'zustand';
import { fetchPlayers, getErrorMessage } from '@/lib/network';
import { toUiPlayer } from '@/lib/players/toUiPlayer';
import { normalizeSearch } from '@/lib/utils';
import { getUserSessionGeneration } from '@/lib/userSessionGeneration';

const PLAYER_FILTER_ALL_POSITION: PlayerFiltersPosition = 'ALL';

export interface UsePlayersState {
  players: Player[];
  isInitialized: boolean;
  isLoading: boolean;
  error: string | undefined;
}

export interface UsePlayersActions {
  init: (
    type?: string,
    tournamentCalendarId?: string,
    userId?: string | null,
    limitFixture?: number,
    preGw?: boolean
  ) => Promise<void>;
  reset: () => void;
  filterBySearch: (players: Player[], searchText: string) => Player[];
  filterByPositions: (players: Player[], positions: PlayerFiltersPosition[]) => Player[];
  filterByTeams: (players: Player[], filters: Array<string | number>) => Player[];
  filterByPrices: (players: Player[], filters: Array<string | number>, budget: number) => Player[];
  sortPlayers: (players: Player[], sortBy: string) => Player[];
}

export type PlayersStore = UsePlayersState & UsePlayersActions;

const initState: UsePlayersState = {
  players: [],
  isInitialized: false,
  isLoading: false,
  error: undefined,
};

const hasAllValue = (values: Array<string | number>) => {
  return values.some(value => value === PlayerFiltersViewDefaultAllOption.value);
};

export const usePlayersStore = create<PlayersStore>((set, get) => {
  const actions: UsePlayersActions = {
    init: async (
      type?: string,
      tournamentCalendarId?: string,
      userId?: string | null,
      limitFixture?: number,
      preGw?: boolean
    ) => {
      const sessionGeneration = getUserSessionGeneration();
      const state = get();
      if (state.isInitialized || state.isLoading) {
        return;
      }

      set({
        isLoading: true,
        error: undefined,
      });

      try {
        const playerType = (type as 'gameweek' | 'matchday') || 'matchday';
        const response = await fetchPlayers(
          tournamentCalendarId,
          playerType,
          userId,
          limitFixture,
          preGw
        );
        const players = response.data.items
          .map(toUiPlayer)
          .filter((player): player is Player => player !== null);

        if (sessionGeneration !== getUserSessionGeneration()) return;

        set({
          players,
          isInitialized: true,
          isLoading: false,
          error: undefined,
        });
      } catch (error) {
        if (sessionGeneration !== getUserSessionGeneration()) return;

        set({
          players: [],
          isInitialized: false,
          isLoading: false,
          error: getErrorMessage(error, 'Failed to initialize players'),
        });
      }
    },
    reset: () => {
      set({
        ...initState,
      });
    },
    filterBySearch: (players, searchText) => {
      const normalizedSearch = normalizeSearch(searchText.trim());
      if (!normalizedSearch) {
        return players;
      }

      return players.filter(player =>
        normalizeSearch(player.displayName).includes(normalizedSearch)
      );
    },
    filterByPositions: (players, positions) => {
      if (!positions.length || positions.includes(PLAYER_FILTER_ALL_POSITION)) {
        return players;
      }

      return players.filter(player => positions.some(position => position === player.position));
    },
    filterByTeams: (players, filters) => {
      if (!filters.length || hasAllValue(filters)) {
        return players;
      }

      const normalizedFilters = filters.map(filter => String(filter).trim().toUpperCase());
      return players.filter(player => {
        const squadId = String(player.squadId ?? '')
          .trim()
          .toUpperCase();
        const shirtKey = String(player.shirtKey ?? '')
          .trim()
          .toUpperCase();
        const clubName = String(player.clubName ?? '')
          .trim()
          .toUpperCase();
        const country = String(player.country ?? '')
          .trim()
          .toUpperCase();
        const contestantFlagKey = String(player.contestantFlagKey ?? '')
          .trim()
          .toUpperCase();
        const teamCandidates = [squadId, shirtKey, clubName, country, contestantFlagKey].filter(
          Boolean
        );

        return normalizedFilters.some(filter => {
          return teamCandidates.some(
            candidate => candidate === filter || candidate.includes(filter)
          );
        });
      });
    },
    filterByPrices: (players, filters, budget) => {
      if (!filters.length || hasAllValue(filters)) {
        return players;
      }

      const maxObservedPrice = players.reduce((max, player) => {
        return Number.isFinite(player.price) ? Math.max(max, player.price) : max;
      }, 0);
      const usesDisplayUnits = maxObservedPrice < 1000;
      const toComparableValue = (value: number) => {
        return usesDisplayUnits ? value : value / 1_000_000;
      };
      const comparableBudget = toComparableValue(budget);

      return players.filter(player => {
        const comparablePrice = toComparableValue(player.price);

        return filters.some(filter => {
          switch (filter) {
            case 'afford':
              return comparablePrice <= comparableBudget;
            case '0-2':
              return comparablePrice >= 0 && comparablePrice <= 2;
            case '2-4':
              return comparablePrice > 2 && comparablePrice <= 4;
            case '4-6':
              return comparablePrice > 4 && comparablePrice <= 6;
            case '6-99':
              return comparablePrice > 6;
            default:
              return false;
          }
        });
      });
    },
    sortPlayers: (players, sortBy) => {
      if (!sortBy) {
        return players;
      }

      const sortedPlayers = [...players];
      sortedPlayers.sort((left, right) => {
        switch (sortBy) {
          case 'price':
            return right.price - left.price;
          case 'percentSelected':
            return right.percentSelected - left.percentSelected;
          case 'totalPoints':
            return right.totalPoints - left.totalPoints;
          default:
            return 0;
        }
      });

      return sortedPlayers;
    },
  };

  return {
    ...initState,
    ...actions,
  };
});
